/**
 * Onboarding Screen
 *
 * Self-service organization creation for first-time users. Anyone who lands
 * here without an org assignment can create one and become its admin. Once
 * an org exists, further orgs are managed via the admin console.
 */

import { Component } from 'react';
import { authenticatedFetch } from '@semantical/services/authenticatedFetch';
import type { AuthContextValue } from '@semantical/contexts/AuthContext';

type Props = {
    auth: AuthContextValue;
};

type State = {
    orgName: string;
    orgSlug: string;
    slugManuallyEdited: boolean;
    isSubmitting: boolean;
    error: string | null;
};

function generateSlug(name: string): string {
    return name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

export class OnboardingScreen extends Component<Props, State> {
    state: State = {
        orgName: '',
        orgSlug: '',
        slugManuallyEdited: false,
        isSubmitting: false,
        error: null,
    };

    handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const orgName = e.target.value;
        this.setState((prev) => ({
            orgName,
            orgSlug: prev.slugManuallyEdited ? prev.orgSlug : generateSlug(orgName),
        }));
    };

    handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            orgSlug: e.target.value,
            slugManuallyEdited: true,
        });
    };

    handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const { orgName, orgSlug } = this.state;

        if (!orgName.trim() || !orgSlug.trim()) {
            this.setState({ error: 'Organization name and slug are required.' });
            return;
        }

        this.setState({ isSubmitting: true, error: null });

        try {
            const res = await authenticatedFetch('/auth/onboarding/organization', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: orgName.trim(), slug: orgSlug.trim() }),
            });

            if (res.ok) {
                await this.props.auth.refreshAuth();
            } else {
                const data = await res.json().catch(() => ({}));
                this.setState({
                    error: data.error || `Failed to create organization (${res.status}).`,
                    isSubmitting: false,
                });
            }
        } catch {
            this.setState({
                error: 'Network error. Please try again.',
                isSubmitting: false,
            });
        }
    };

    render() {
        const { auth } = this.props;
        const { orgName, orgSlug, isSubmitting, error } = this.state;
        const userName = auth.user?.name || auth.user?.email || 'there';

        return (
            <div className="flex min-h-screen items-center justify-center bg-[#1a1a2e]">
                <div className="mx-4 w-full max-w-md">
                    <div className="rounded-lg border border-white/10 bg-[#16213e] p-8 shadow-xl">
                        <h1 className="mb-2 text-2xl font-semibold text-white">Welcome, {userName}</h1>
                        <p className="mb-6 text-white/60">Create your organization to get started with Semantical.</p>

                        <form onSubmit={this.handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="org-name" className="mb-1.5 block text-sm font-medium text-white/80">
                                    Organization Name
                                </label>
                                <input
                                    id="org-name"
                                    type="text"
                                    value={orgName}
                                    onChange={this.handleNameChange}
                                    placeholder="My Organization"
                                    className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-white placeholder-white/30 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                    disabled={isSubmitting}
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label htmlFor="org-slug" className="mb-1.5 block text-sm font-medium text-white/80">
                                    URL Slug
                                </label>
                                <input
                                    id="org-slug"
                                    type="text"
                                    value={orgSlug}
                                    onChange={this.handleSlugChange}
                                    placeholder="my-organization"
                                    className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 font-mono text-sm text-white placeholder-white/30 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                    disabled={isSubmitting}
                                />
                                <p className="mt-1 text-xs text-white/40">
                                    Used in URLs. Lowercase letters, numbers, and hyphens only.
                                </p>
                            </div>

                            {error && (
                                <div
                                    className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400"
                                    role="alert"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting || !orgName.trim() || !orgSlug.trim()}
                                className="w-full rounded-md bg-blue-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-[#16213e] disabled:cursor-not-allowed disabled:bg-blue-600/40"
                            >
                                {isSubmitting ? 'Creating...' : 'Create Organization'}
                            </button>
                        </form>

                        <div className="mt-6 border-t border-white/10 pt-4 text-center">
                            <button
                                onClick={() => auth.logout()}
                                className="text-sm text-white/40 transition-colors hover:text-white/60"
                            >
                                Sign out
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
